import { defineStore } from 'pinia'
import { ref } from 'vue'
import { User, UserMap } from '@/types/user'
import { userService } from '@/services/userService'
import {
  organizationService,
} from '@/services/organizationService'
import { fetchMe } from '@/services/authService'
import { UserStoreState } from '@/types/states/users'
import { useTeamStore } from '@/stores/teamStore'
import { TeamUser } from '@/types/teamUser'
import { deriveTeamMembershipsFromRoles } from '@/composables/user'

export const useUserStore = defineStore(
  'user',
  () => {
    // State
    const users = ref<UserMap>({})
    const currentUser = ref<User | null>(null)
    const isCurrentUserReady = ref(false)
    const teamStore = useTeamStore()

    // Actions
    function setUsers(userArray: User[]) {
      users.value = userArray.reduce((map: UserMap, user: User) => {
        map[user.id] = user
        return map
      }, {} as UserMap)
    }

    function setCurrentUser(user: User | null) {
      currentUser.value = user
      if (user) {
        localStorage.setItem('currentUser', JSON.stringify(user))
      }
    }

    function addUser(user: User) {
      users.value[user.id] = user
    }

    async function getUserById(userId: string, allowCache = true): Promise<User | undefined> {
      if (allowCache && users.value[userId]) {
        return users.value[userId]
      } else {
        const user = await userService.fetchById(userId)
        if (user) {
          users.value[user.id] = user
          return user
        }
      }
      return undefined
    }

    function updateUser(updatedUser: User) {
      users.value[updatedUser.id] = updatedUser
      if (currentUser.value?.id === updatedUser.id) {
        currentUser.value = updatedUser
      }
    }

    function removeUser(userId: string) {
      delete users.value[userId]
      if (currentUser.value?.id === userId) {
        currentUser.value = null
      }
    }

    async function loadCurrentUser(this: UserStoreState): Promise<boolean> {
      // Mark the user as not-ready while we rehydrate so reactive consumers
      // (e.g. the sidebar menu) do not compute off a partially populated user.
      isCurrentUserReady.value = false
      // Fetch current user details from /v2/auth/me. The response is the
      // user object itself; each role inside `roles` carries its own
      // `context` map (teams/organizations it applies to).
      try {
        const user = await fetchMe()
        currentUser.value = { ...user }
      } catch (error: any) {
        console.error('Failed to fetch current user details', error)
      }
      if (!this.currentUser) {
        return false
      }

      // Fetch user organization
      try {
        if (this.currentUser) {
          this.currentUser.organization = await organizationService.fetchCurrentUserOrganization()
        } else {
          return false
        }
      } catch (error: any) {
        console.error('Failed to fetch user organization', error)
      }

      // Team memberships derive from the RBAC role context, not the
      // /team_users endpoint (which now returns a team's member users). See
      // loadCurrentUserRBAC for the full rationale.
      const teamUsers: TeamUser[] = deriveTeamMembershipsFromRoles(this.currentUser)
      this.currentUser.teamUsers = teamUsers

      // Load teams into teamStore
      try {
        const teamIds = teamUsers.map((tu) => tu.teamId).filter((id) => id) as string[]
        await teamStore.loadTeamsFromApi(teamIds)
      } catch (error: any) {
        console.error('Failed to load teams into teamStore', error)
      }

      isCurrentUserReady.value = true
      return true
    }

    async function loadCurrentUserRBAC(this: UserStoreState): Promise<boolean> {
      console.log('[sidebar-debug] loadCurrentUserRBAC START')
      isCurrentUserReady.value = false

      // Step 1: fetch the base user via /auth/me. The response is the user
      // object; roles[].context carries the per-role entity scoping.
      let baseUser: User | null = null
      try {
        baseUser = await fetchMe()
      } catch (error: any) {
        console.error('Failed to fetch current user details', error)
      }
      if (!baseUser) {
        return false
      }

      // Step 2: team memberships come from the RBAC role context
      // (roles[].context.teams) - the source of truth since the team_users
      // join table was dropped. The /users/{id}/team_users endpoint now
      // returns a team's member *users*, not this user's memberships, so it
      // must NOT be used to populate teamUsers. Organization is still fetched
      // from its own endpoint.
      const fetchedTeamUsers: TeamUser[] = deriveTeamMembershipsFromRoles(baseUser)

      let organization: User['organization'] | undefined
      try {
        organization = await organizationService.fetchCurrentUserOrganization()
      } catch (error: any) {
        console.error('Failed to fetch user organization', error)
      }

      console.log('[sidebar-debug] after parallel fetch', {
        rolesLen: baseUser.roles?.length,
        teamUsersLen: fetchedTeamUsers.length,
        hasOrg: !!organization,
      })

      // Step 3: assemble the fully-hydrated user once and commit atomically.
      // (The legacy organizationUser fetch is gone - org-scoped roles come
      // from the RBAC context in baseUser.roles.)
      const draft: User = {
        ...baseUser,
        organization,
        teamUsers: fetchedTeamUsers,
      }
      currentUser.value = draft
      isCurrentUserReady.value = true
      console.log('[sidebar-debug] currentUser committed', {
        rolesLen: draft.roles?.length,
        teamUsersLen: draft.teamUsers?.length,
      })

      // Step 4: load teams into teamStore
      try {
        const teamIds = fetchedTeamUsers.map((tu) => tu.teamId).filter((id) => id) as string[]
        await teamStore.loadTeamsFromApi(teamIds)
      } catch (error: any) {
        console.error('Failed to load teams into teamStore', error)
      }

      return true
    }

    function clear() {
      users.value = {}
      currentUser.value = null
      isCurrentUserReady.value = false
      localStorage.removeItem('currentUser')
    }

    return {
      users,
      currentUser,
      isCurrentUserReady,
      setUsers,
      setCurrentUser,
      addUser,
      getUserById,
      updateUser,
      removeUser,
      loadCurrentUser,
      loadCurrentUserRBAC,
      clear,
    }
  },
  {
    persist: {
      key: 'userStore',
      storage: localStorage,
    },
  },
)
